import React, { useState, useEffect } from 'react';
import { X, Search, FileText, Image as ImageIcon, Video, Music } from 'lucide-react';
import api from '../../utils/api';

const MediaPickerModal = ({ isOpen, onClose, onSelect }) => {
  const [files, setFiles] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchFiles();
    }
  }, [isOpen, activeCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const res = await api('/media-library/categories');
      if (res.ok) {
        const data = await res.json();
        setCategories(['All', ...data]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (activeCategory !== 'All') queryParams.append('category', activeCategory);
      if (searchQuery) queryParams.append('search', searchQuery);

      const res = await api(`/media-library?${queryParams.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setFiles(data);
      }
    } catch (err) {
      console.error('Failed to fetch media files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (file) => {
    try {
      // Optimistically increment usage
      await api(`/media-library/${file._id}/use`, { method: 'PATCH' });
    } catch (e) {
      console.error('Failed to update usage count');
    }
    onSelect(file);
    onClose();
  };

  const renderIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <ImageIcon size={24} color="#3b82f6" />;
      case 'video': return <Video size={24} color="#ef4444" />;
      case 'audio': return <Music size={24} color="#8b5cf6" />;
      default: return <FileText size={24} color="#10b981" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Select Media from Library</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
            <X size={24} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={18} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }}
            />
          </div>
          <select 
            value={activeCategory} 
            onChange={(e) => setActiveCategory(e.target.value)}
            style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', minWidth: '150px' }}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '4px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-tertiary)' }}>Loading...</div>
          ) : files.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-tertiary)' }}>No media found.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
              {files.map(file => (
                <div 
                  key={file._id}
                  onClick={() => handleSelect(file)}
                  style={{ 
                    border: '1px solid var(--color-border-primary)', 
                    borderRadius: '8px', 
                    padding: '12px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s',
                    ':hover': { borderColor: 'var(--color-brand-primary)', background: 'var(--color-background-secondary)' }
                  }}
                >
                  <div style={{ height: '100px', background: 'var(--color-background-secondary)', borderRadius: '6px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {file.fileType === 'image' ? (
                      <img src={file.fileUrl} alt={file.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      renderIcon(file.fileType)
                    )}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }} title={file.fileName}>
                    {file.fileName}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                    <span>{file.category}</span>
                    <span>{(file.fileSize / 1024 / 1024).toFixed(1)} MB</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaPickerModal;
